import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Hidden,
  IconButton,
  Skeleton,
  Theme,
  Typography,
} from '@mui/material';
import * as analytics from 'analytics';
import * as uuid from 'uuid';

import { SECONDARY_BANK_LOGIN_URLS } from '@/config/env';
import { FormEventType } from '@/legacy/features/analytics/helper';
import * as secondaryBankLoginService from '@/legacy/features/companies/bankLogin/services/secondaryBankLogin';
import * as companyService from '@/legacy/services/company';
import * as bankLoginService from '@/legacy/services/company/bankLogin';
import CompanyEventService from '@/legacy/services/companyEventService';
import { Action } from '@/legacy/utils/reactHelpers';
import { invalidateReactQueriesFromDurandal } from '@/utils/invalidateReactQueriesFromDurandal';

import SecondaryBankLoginEventName from '../analytics/helper';
import BankLoginSource from '../bankLoginSource';

import { SecondaryBankAccountDetailsAddModel } from './viewModels';

/**
 * Set by Product and business requirements.
 */
const TAKING_TOO_LONG_TIMEOUT = 180 * 1000;

enum ActionType {
  SelectAccount = 'SelectAccount',
  ProceedWithSelectedAccount = 'ProceedWithSelectedAccount',
  LoadAccountSelection = 'LoadAccountSelection',
  PollAccountDetails = 'PollAccountDetails',
  SelectBankInstitution = 'LoadBankInstitutionSelection',

  SubmitCredentials = 'SubmitCredentials',
  LoadCredentials = 'LoadCredentials',
  SubmitMultiFactorAuthentication = 'SubmitMultiFactorAuthentication',
  LoadMultiFactorAuthentication = 'LoadMultiFactorAuthentication',
  InstitutionSelected = 'InstitutionSelected',

  SetAuthorizationKey = 'SetAuthorizationKey',

  QueryPollAccountDetails = 'QueryPollAccountDetails',

  CloseDialog = 'CloseDialog',
  QueryCloseDialogCallback = 'QueryCloseDialogCallback',

  ForceVoidCheque = 'ForceVoidCheque',
  QueryForceVoidChequeCallback = 'QueryForceVoidChequeCallback',
  QueryAuthorizationKey = 'QueryAuthorizationKey',
}

interface State {
  isBankAccountSelected: boolean;
  loginId: string;
  requestId: string;
  accountId: string;
  secondaryBankPollingResponse: secondaryBankLoginService.SecondaryBankPollResponse;
  pollingCount: number;
  pollingRate: number;
  bankCountry: string;
  relatedId?: string;
  bankLoginSource: BankLoginSource;
  uniqueRequestIdentifer: string;
  authorizationKey?: string;

  queries: Array<Action<ActionType>>;
}

enum SecondaryBankEvent {
  ACCOUNT_SELECTED = 'ACCOUNT_SELECTED',
  REDIRECT = 'REDIRECT',
  INSTITUTION_SELECTED = 'INSTITUTION_SELECTED',
  SUBMIT_CREDENTIAL = 'SUBMIT_CREDENTIAL',
  COMPONENT_LOAD_CREDENTIAL = 'COMPONENT_LOAD_CREDENTIAL',
  SUBMIT_MFA = 'SUBMIT_MFA',
  COMPONENT_LOAD_MFA = 'COMPONENT_LOAD_MFA',
  COMPONENT_LOAD_ACCOUNT_SELECTION = 'COMPONENT_LOAD_ACCOUNT_SELECTION',
  COMPONENT_LOAD_INSTITUTION_SELECTOR = 'COMPONENT_LOAD_INSTITUTION_SELECTOR',
}

const localConfig = {
  maxPollingCount: 60,

  iframeOptions: {
    version: 2,
    customerName: 'Plooto',
    language: 'en',
    theme: 'light',
    backgroundColor: 'F4F4F4',
    foregroundColor1: '000000',
    fixedHeightEnable: false,
    desktopLayout: true,

    /**
     * Remove the lock image and text from the header
     */
    headerEnable: false,

    /**
     * To add a search bar into the Institution Selection Menu, you need to specify the parameter institutionFilterEnable=true.
     */
    institutionFilterEnable: true,

    /**
     * If set to true enables the Flinks Connect bank for usage
     */
    demo: false,

    /**
     * If set to false then don't fetch account transactions (faster)
     */
    withTransactions: false,

    /**
     * Flinks Connect extracts Transactions history data for the last 90 days
     */
    daysOfTransactions: 'Days90',

    /**
     * Adds user consent step (false to remove)
     */
    consentEnable: false,

    /**
     * Add the parameter accountSelectorEnable=true to enable the Account Selection feature in Flinks Connect.
     */
    accountSelectorEnable: true,

    /**
     * If you want to present your end user all operations accounts, even those not eligible for EFT, use the parameter showAllOperationsAccounts=true.
     */
    showAllOperationsAccounts: false,

    /**
     * By default, Flinks Connect only collects the details of the selected accounts, but it is possible to still gather all accounts details by specifying the parameter fetchAllAccounts=true.
     */
    fetchAllAccounts: false,

    /**
     * If your use-cases requires automatic account refresh, we recommended you use the Enhanced MFA flow. To activate this feature, the parameter enhancedMFA=true must be specified.
     */
    enhancedMFA: false,

    /**
     * By default, only a single account can be selected. In order to enable multiple accounts to be selected, the parameter accountSelectorMultiple=true needs to be set.
     */
    accountSelectorMultiple: false,

    /**
     * This would ignore additional KYC related properties
     */
    withKYC: false,

    /**
     * This will not display the account balance when the process is completed
     */
    withBalance: false,
  },
};

const Styles = {
  dialogContent: {
    flex: 1,

    /**
     * Even though we specify a height the container is limited by max-height.
     * This height ensures the popup spans a minimum height
     */
    height: 1024,
    minHeight: 400,
    padding: 0,
  },
  frameContainer: {
    display: 'flex',
    width: '100%',
    height: 396,
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heading: (theme: Theme) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
  }),
  manualEntry: {
    padding: 0,
    '& .MuiLink-root': {
      fontWeight: 600,
      color: '#fff',
    },
  },
  manualCard: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 2,
    '&:last-child': {
      pb: 2,
    },
  },
  manualCardContainer: {
    border: '1px solid #DAE1E9',
    margin: 3,
    width: '100%',
  },
  subheading: (theme: Theme) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
  }),
  typographyContainer: {
    minWidth: 180,
    width: '75%',
  },
};

interface BottomCardProps {
  dispatch: React.Dispatch<Action<ActionType>>;
  shouldDisplayTakingTooLong: boolean;
}

const BottomCard: React.FC<BottomCardProps> = ({ dispatch, shouldDisplayTakingTooLong }) => {
  const headingText = shouldDisplayTakingTooLong ? (
    <FormattedMessage defaultMessage="Is your bank taking too long?" />
  ) : (
    <FormattedMessage defaultMessage="Manually connect using Bank Account details" />
  );

  const subheadingText = shouldDisplayTakingTooLong ? (
    <FormattedMessage defaultMessage="Try manually connecting using your account details now." />
  ) : (
    <FormattedMessage defaultMessage="Manually enter your transit, institution, and account number to connect instead." />
  );

  return (
    <Card sx={Styles.manualCardContainer} elevation={0}>
      <CardContent sx={Styles.manualCard}>
        <Box display="flex" flexDirection="column">
          <Typography sx={Styles.heading}>{headingText}</Typography>

          <Typography sx={Styles.subheading}>{subheadingText}</Typography>
        </Box>

        <Button onClick={() => dispatch({ type: ActionType.ForceVoidCheque })} variant="text">
          <Typography sx={Styles.heading}>
            <FormattedMessage defaultMessage="Connect" />
          </Typography>
        </Button>
      </CardContent>
    </Card>
  );
};

interface RenderProps {
  dispatch: React.Dispatch<Action<ActionType>>;
  isBankAccountSelected: boolean;
  authorizationKey?: string;
  bankCountry: string;
  shouldDisplayTakingTooLong: boolean;
  shouldRenderFrameOnly: boolean;
}

const Render: React.FC<RenderProps> = ({
  dispatch,
  isBankAccountSelected,
  bankCountry,
  authorizationKey,
  shouldDisplayTakingTooLong,
  shouldRenderFrameOnly,
}: RenderProps) => {
  const { formatMessage } = useIntl();
  // Combine the iFrame options object into a URI
  const baseUrl = SECONDARY_BANK_LOGIN_URLS[bankCountry];

  const iframeOptions = {
    ...localConfig.iframeOptions,
    authorizeToken: authorizationKey,
  };
  const [isIframeLoading, setIsIframeLoading] = React.useState(true);
  const iframeSrc = Object.entries(iframeOptions).reduce(
    (uri, option) => `${uri ? `${uri}&` : `${baseUrl}?`}${option[0]}=${option[1]}`,
    ''
  );

  const getDialogContent = (): React.JSX.Element => {
    if (!authorizationKey) {
      return <Skeleton width="100%" height="100%" />;
    }

    if (isBankAccountSelected) {
      return <Skeleton width="100%" height="100%" />;
    }

    return (
      <Box flexGrow={1} height="100%" width="100%">
        {isIframeLoading ? <Skeleton width="100%" height="100%" /> : null}
        <iframe
          onLoad={() => setIsIframeLoading(false)}
          src={iframeSrc}
          style={{
            border: 'none',
            display: isIframeLoading ? 'none' : 'initial',
            flexGrow: 1,
            margin: 0,
            padding: 0,
            width: '100%',
            height: '100%',
          }}
          title={formatMessage({
            defaultMessage: 'Select your bank and authorize the transaction',
          })}
        />
      </Box>
    );
  };

  if (shouldRenderFrameOnly) {
    return <Box sx={Styles.frameContainer}>{getDialogContent()}</Box>;
  }

  return (
    <Dialog aria-labelledby="bankLogin-dialog-title" fullWidth maxWidth="sm" open scroll="paper">
      <DialogTitle id="bankLogin-dialog-title">
        <Grid container justifyContent="space-between">
          <Grid item>
            <Hidden only={['xs', 'sm']}>
              <FormattedMessage defaultMessage="Connect To Your Bank Account" />
              <i className="fa fa-lock text-plooto-blue pl-du-1" aria-hidden="true" />
            </Hidden>
            <Hidden only={['xl', 'lg', 'md']}>
              <FormattedMessage defaultMessage="Connect Your Account" />{' '}
              <i className="fa fa-lock text-plooto-blue pl-du-1" aria-hidden="true" />
            </Hidden>
          </Grid>
          <Grid item>
            <IconButton size="small" onClick={() => dispatch({ type: ActionType.CloseDialog })}>
              <span className="pl-icon pl-icon-3 svg-white-modal-close" />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers sx={Styles.dialogContent}>
        <Box sx={Styles.frameContainer}>{getDialogContent()}</Box>
      </DialogContent>
      <DialogActions sx={Styles.manualEntry}>
        <BottomCard dispatch={dispatch} shouldDisplayTakingTooLong={shouldDisplayTakingTooLong} />
      </DialogActions>
    </Dialog>
  );
};

interface ProceedWithSelectedAccountPayload {
  institution: string;
  loginId: string;
  requestId: string;
}

interface QueryPollAccountDetails {
  loginId: string;
  requestId: string;
  accountId: string;
  pollingRate: number;
  pollingCount: number;
  country: string;
  bankLoginSource: BankLoginSource;
  secondaryBankPollingResponse: secondaryBankLoginService.SecondaryBankPollResponse;
  relatedId?: string;
  institution?: string;
}

const getPollingRate = (pollingCount: number) => {
  // First poll is 3 seconds
  if (pollingCount === 0) {
    return 3000;
  }

  if (pollingCount > 0 && pollingCount <= 30) {
    return 5000;
  }
  return 10000;
};

const trackUserAction = (state: State, action: Action<ActionType>) => {
  const { type, payload } = action;
  const getRelatedIdParam = () => {
    // Add property depending on bank validation source (EMT / Payment Request)
    switch (state.bankLoginSource) {
      case BankLoginSource.EmailMoneyTransfer:
        return { emailMoneyTransferId: state.relatedId };
      case BankLoginSource.PaymentRequest:
        return { paymentRequestId: state.relatedId };
      default:
        break;
    }

    return {};
  };

  const analyticsPayload = {
    country: state.bankCountry,
    bankLoginSource: state.bankLoginSource,
    data: payload,
    event: type,
    ...getRelatedIdParam(),
  };

  const analyticsPayloadJson = JSON.stringify(analyticsPayload);

  const trackAction = (message: string) => {
    analytics.send('event', 'Secondary-Bank-Validation', message, analyticsPayloadJson);
  };

  const trackSegmentAction = (
    eventType: SecondaryBankLoginEventName | FormEventType,
    eventPayload: object
  ) => {
    CompanyEventService.emit(eventType, eventPayload);
  };

  switch (type) {
    case ActionType.ForceVoidCheque: {
      trackSegmentAction(SecondaryBankLoginEventName.AutomaticBankVerificationForceVoidCheque, {
        buttonName: 'Go to Manual Bank Verification',
        companyId: companyService.Instance.CompanyId(),
        parentNodeId: companyService.Instance.AccountingFirmCompanyId(),
        bankVerificationAttemptId: state.uniqueRequestIdentifer,
        context: 'Automatic Bank Verification',
      });

      trackAction('Selected void physical check');
      break;
    }

    case ActionType.SelectAccount:
      trackAction('Selected bank account');
      break;

    case ActionType.CloseDialog: {
      trackAction('Closed bank selection modal');

      trackSegmentAction(SecondaryBankLoginEventName.AutomaticBankVerificationCancelled, {
        companyId: companyService.Instance.CompanyId(),
        parentNodeId: companyService.Instance.AccountingFirmCompanyId(),
        bankCountry: state.bankCountry,
        bankVerificationAttemptId: state.uniqueRequestIdentifer,
      });

      break;
    }

    case ActionType.ProceedWithSelectedAccount:
      trackAction('Proceeded with account selection');
      break;

    case ActionType.SubmitCredentials: {
      trackSegmentAction(SecondaryBankLoginEventName.AutomaticBankConnectionSubmitted, {
        companyId: companyService.Instance.CompanyId(),
        parentNodeId: companyService.Instance.AccountingFirmCompanyId(),
        bankCountry: state.bankCountry,
        bankInstitution: payload?.institution,
        bankVerificationAttemptId: state.uniqueRequestIdentifer,
      });

      trackAction('Submitted banking credentials');

      break;
    }
    case ActionType.SubmitMultiFactorAuthentication:
      trackAction('Submitted multi-factor authentication');
      break;

    case ActionType.InstitutionSelected:
      trackAction('Selected banking institution');
      break;

    case ActionType.LoadAccountSelection:
      trackAction('Load bank account selection');
      break;

    case ActionType.LoadCredentials:
      trackAction('Load bank login credentials');
      break;

    case ActionType.LoadMultiFactorAuthentication:
      trackAction('Load multi-factor authentication');
      break;

    case ActionType.SelectBankInstitution: {
      trackSegmentAction(SecondaryBankLoginEventName.AutomaticBankConnectionInitiated, {
        companyId: companyService.Instance.CompanyId(),
        parentNodeId: companyService.Instance.AccountingFirmCompanyId() ?? 'N/A',
        bankVerificationAttemptId: state.uniqueRequestIdentifer,
      });

      trackAction('Load bank institution selection');
      break;
    }

    default:
      break;
  }
};

const reducer: React.Reducer<State, Action<ActionType>> = (state, action) => {
  const { type, payload } = action;

  // Track actions in GA
  try {
    trackUserAction(state, action);
  } catch (err) {
    // Silently fail if there was an error tracking a user action instead of breaking the bank login
  }

  switch (type) {
    case ActionType.SelectAccount:
      return {
        ...state,
        accountId: payload,
      };

    case ActionType.CloseDialog:
      return {
        ...state,
        queries: [{ type: ActionType.QueryCloseDialogCallback }],
      };

    case ActionType.ForceVoidCheque:
      return {
        ...state,
        queries: [{ type: ActionType.QueryForceVoidChequeCallback }],
      };

    case ActionType.SetAuthorizationKey: {
      const { authorizationKey } = payload;

      return {
        ...state,
        authorizationKey,
      };
    }

    case ActionType.PollAccountDetails: {
      const { requestId, loginId, pollingCount, secondaryBankPollingResponse } =
        payload as QueryPollAccountDetails;

      const updatedPollingRate = getPollingRate(pollingCount);

      const queries = [
        {
          type: ActionType.QueryPollAccountDetails,
          payload: {
            ...payload,

            pollingRate: updatedPollingRate,
            secondaryBankPollingResponse,
          } as QueryPollAccountDetails,
        },
      ];

      return {
        ...state,
        loginId,
        requestId,
        pollingCount,
        pollingRate: updatedPollingRate,
        secondaryBankPollingResponse,
        queries,
      };
    }

    case ActionType.ProceedWithSelectedAccount: {
      const { accountId, pollingRate, pollingCount, bankCountry, bankLoginSource, relatedId } =
        state;
      const { loginId, requestId, institution } = payload as ProceedWithSelectedAccountPayload;

      return {
        ...state,
        loginId,
        requestId,
        accountId,
        isBankAccountSelected: true,
        queries: [
          {
            // First attempt does not include request id, subsequent calls will have the request id while waiting for final response
            // We're ready to call the polling endpoint
            type: ActionType.QueryPollAccountDetails,
            payload: {
              loginId,
              accountId,
              requestId,
              pollingCount,
              country: bankCountry,
              pollingRate,
              secondaryBankPollingResponse: null,
              bankLoginSource,
              relatedId,
              institution,
            } as QueryPollAccountDetails,
          },
        ],
      };
    }

    default:
      break;
  }

  return state;
};

interface Props {
  // ??? We use these in the queries pattern.
  // eslint-disable-next-line react/no-unused-prop-types
  closeDialogCallback: () => void;
  // ??? We use these in the queries pattern.
  // eslint-disable-next-line react/no-unused-prop-types
  accountSelectedCallback: (
    secondaryBankAccountDetailsAddModel: SecondaryBankAccountDetailsAddModel
  ) => void;
  // ??? We use these in the queries pattern.
  // eslint-disable-next-line react/no-unused-prop-types
  forceVoidChequeCallback: (voidChequeText: string) => void;
  bankCountry: string;

  bankLoginSource: BankLoginSource;
  isEmtFlow?: boolean;
  /**
   * What is the related object id based on bankLoginSource?
   *
   * We will pass this relatedId when generating authorization key.
   */
  relatedId: string;
  /**
   * True if only the Flinks iframe should be rendered, versus a dialog container. Used in the FPay
   * Wizard.
   */
  shouldRenderFrameOnly?: boolean;
  /**
   * Custom error-handling callback. Should handle the same cases as
   * BankLoginService.handleAddBankAccountError(). Use this to present errors in a different way,
   * i.e. something other than titleMessageModal.
   */
  // ??? We use these in the queries pattern.
  // eslint-disable-next-line react/no-unused-prop-types
  handleBankError: (error: unknown) => void;
}

const handleQueries = async (
  queries: Array<Action<ActionType>>,
  dispatch: React.Dispatch<Action<ActionType>>,
  props: Props
) => {
  const handleBankError =
    props.handleBankError ??
    ((error: unknown) => {
      const errorDetails = bankLoginService.Instance.handleAddBankAccountError({
        error,
        forceManualEntryCallback: (errorText) => {
          props.forceVoidChequeCallback(errorText);
        },
      });

      // Close the popup if there is a dialog and we're not redirecting back to manual entry
      // This is due to the fact that the React dialog blocks the Durandal dialog
      if (
        (errorDetails.dialog && errorDetails.fallbackToManualEntry === undefined) ||
        errorDetails.closeDialog
      ) {
        dispatch({ type: ActionType.CloseDialog });
      }
    });

  queries.forEach(async ({ payload, type }) => {
    switch (type) {
      case ActionType.QueryAuthorizationKey:
        try {
          const { relatedId, bankLoginSource, bankCountry } = props;

          const authorizationKey = await secondaryBankLoginService.Instance.getAuthorizationKey({
            relatedId,
            bankLoginSource,
            country: bankCountry,
          });

          dispatch({ type: ActionType.SetAuthorizationKey, payload: { authorizationKey } });
        } catch (err) {
          handleBankError(err);
        }
        break;

      case ActionType.QueryPollAccountDetails: {
        const {
          accountId,
          loginId,
          requestId,
          pollingCount,
          relatedId,
          pollingRate,
          country,
          bankLoginSource,
          institution,
        } = payload as QueryPollAccountDetails;

        // After 60 attempts, force to manual upload, bank provider timed out
        if (pollingCount >= localConfig.maxPollingCount) {
          handleBankError({ error: true, type: 'provider.timeout' });
          return;
        }

        try {
          const pollRequestId = pollingCount === 0 ? null : requestId; // For the first poll don't use a requestId (this will avoid using wrong requestId from iFrame)
          const pollResponse =
            await secondaryBankLoginService.Instance.pollSecondaryBankAccountDetails({
              loginId,
              requestId: pollRequestId,
              accountId,
              country,
              relatedId,
              bankLoginSource,
            });

          // If account was retrieved, then stop polling and submit selected account
          if (!pollResponse.account) {
            const updatedPollingCount = pollingCount + 1;

            setTimeout(async () => {
              dispatch({
                type: ActionType.PollAccountDetails,
                payload: {
                  ...payload,
                  requestId: pollResponse.requestId,
                  pollingCount: updatedPollingCount,
                  secondaryBankPollingResponse: pollResponse,
                },
              });
            }, pollingRate); // First poll is 3 seconds, subsequent polls are timed 5 to 30 seconds
          } else {
            // Account is now selected, send it to the callback
            const secondaryBankAccountDetailsAddModel = new SecondaryBankAccountDetailsAddModel();
            secondaryBankAccountDetailsAddModel.account = pollResponse.account;
            secondaryBankAccountDetailsAddModel.requestId = pollResponse.requestId;
            secondaryBankAccountDetailsAddModel.currency = pollResponse.account.currency;

            secondaryBankAccountDetailsAddModel.discriminator =
              'SecondaryBankAccountDetailsAddModel';
            secondaryBankAccountDetailsAddModel.loginId = loginId;
            secondaryBankAccountDetailsAddModel.accountNumber = null; // account number not required
            secondaryBankAccountDetailsAddModel.accountId = accountId;
            secondaryBankAccountDetailsAddModel.country = country;
            secondaryBankAccountDetailsAddModel.relatedId = relatedId;
            secondaryBankAccountDetailsAddModel.institution = institution;
            props.accountSelectedCallback(secondaryBankAccountDetailsAddModel);
          }
        } catch (error) {
          handleBankError(error);
        }
        break;
      }

      case ActionType.QueryCloseDialogCallback:
        invalidateReactQueriesFromDurandal([
          'companies',
          companyService.Instance.CompanyId(),
          'onboarding',
          'status',
        ]);
        invalidateReactQueriesFromDurandal([
          'companies',
          companyService.Instance.CompanyId(),
          'verification',
          'outstandingVerificationItems',
        ]);
        props.closeDialogCallback();
        break;

      case ActionType.QueryForceVoidChequeCallback:
        props.forceVoidChequeCallback(null);
        break;

      default:
        break;
    }
  });
};

const SecondaryBankLoginFragment: React.FC<Props> = (props: Props) => {
  const {
    bankCountry,
    bankLoginSource,
    isEmtFlow = false,
    relatedId,
    shouldRenderFrameOnly,
  } = props;

  const [credentialsSubmitted, setCredentialsSubmitted] = React.useState(false);

  const [shouldDisplayTakingTooLong, setShouldDisplayTakingTooLong] = React.useState(false);

  const [takingTooLongTimeout, setTakingTooLongTimeout] = React.useState<number>();

  const [disableTakingTooLongTimeout, setDisableTakingTooLongTimeout] = React.useState(false);

  React.useEffect(() => {
    if (credentialsSubmitted && isEmtFlow) {
      const timeoutId = setTimeout(
        () => setShouldDisplayTakingTooLong(true),
        TAKING_TOO_LONG_TIMEOUT
      );
      // ??? Our TS thinks we're in Node land and not browser land.
      setTakingTooLongTimeout(timeoutId as unknown as number);
    }
  }, [credentialsSubmitted, isEmtFlow]);

  React.useEffect(() => {
    if (takingTooLongTimeout && disableTakingTooLongTimeout) {
      clearTimeout(takingTooLongTimeout);
    }
  }, [disableTakingTooLongTimeout, takingTooLongTimeout]);

  const initialState: State = {
    accountId: '',
    loginId: '',
    requestId: '',
    pollingCount: 0,
    pollingRate: getPollingRate(0),
    secondaryBankPollingResponse: null,
    bankCountry,
    relatedId,
    bankLoginSource,
    isBankAccountSelected: false,
    authorizationKey: null,
    uniqueRequestIdentifer: uuid.v4(),
    queries: [
      // [PLP-34129] For US countries automatically force void check
      // If not US then start by querying for an authorization key
      ...(bankCountry === 'US'
        ? [{ type: ActionType.QueryForceVoidChequeCallback }]
        : [{ type: ActionType.QueryAuthorizationKey }]),
    ],
  };

  const [state, dispatch] = React.useReducer(reducer, initialState);

  React.useEffect(() => {
    handleQueries(state.queries, dispatch, props);
  }, [state.queries, props]);

  React.useEffect(() => {
    const processFlinksMessage = (e) => {
      switch (e.data.step) {
        case SecondaryBankEvent.ACCOUNT_SELECTED:
          setDisableTakingTooLongTimeout(true);
          dispatch({ type: ActionType.SelectAccount, payload: e.data?.accountId });
          break;

        case SecondaryBankEvent.REDIRECT:
          setDisableTakingTooLongTimeout(true);
          dispatch({
            type: ActionType.ProceedWithSelectedAccount,
            payload: e.data as ProceedWithSelectedAccountPayload,
          });
          break;

        case SecondaryBankEvent.SUBMIT_CREDENTIAL:
          setCredentialsSubmitted(true);
          trackUserAction(state, { type: ActionType.SubmitCredentials, payload: e.data });
          break;

        case SecondaryBankEvent.SUBMIT_MFA:
          trackUserAction(state, {
            type: ActionType.SubmitMultiFactorAuthentication,
            payload: e.data,
          });
          break;

        case SecondaryBankEvent.INSTITUTION_SELECTED:
          trackUserAction(state, { type: ActionType.InstitutionSelected, payload: e.data });
          break;

        case SecondaryBankEvent.COMPONENT_LOAD_CREDENTIAL:
          trackUserAction(state, { type: ActionType.LoadCredentials, payload: e.data });
          break;

        case SecondaryBankEvent.COMPONENT_LOAD_MFA:
          setDisableTakingTooLongTimeout(true);
          trackUserAction(state, {
            type: ActionType.LoadMultiFactorAuthentication,
            payload: e.data,
          });
          break;

        case SecondaryBankEvent.COMPONENT_LOAD_ACCOUNT_SELECTION:
          trackUserAction(state, { type: ActionType.LoadAccountSelection, payload: e.data });
          break;

        case SecondaryBankEvent.COMPONENT_LOAD_INSTITUTION_SELECTOR:
          trackUserAction(state, { type: ActionType.SelectBankInstitution, payload: e.data });
          break;

        default:
          break;
      }
    };

    const onWindowMessage = (e) => {
      if (!!e && !!e.data && !!e.data.step) {
        processFlinksMessage(e);
      }
    };
    window.addEventListener('message', onWindowMessage);

    return () => {
      window.removeEventListener('message', onWindowMessage);
    };
  }, [state]);

  return (
    <Render
      dispatch={dispatch}
      isBankAccountSelected={state.isBankAccountSelected}
      authorizationKey={state.authorizationKey}
      bankCountry={bankCountry}
      shouldDisplayTakingTooLong={shouldDisplayTakingTooLong}
      shouldRenderFrameOnly={shouldRenderFrameOnly}
    />
  );
};

export default SecondaryBankLoginFragment;
